import { Folder, FolderOpen, FolderPlus, MessageSquare, Plus, Settings } from 'lucide-react';
import { useMemo, useState } from 'react';
import { isNonNullish, unique } from 'remeda';
import {
  type ModalHeaderAction,
  ModalList,
  type ModalListItem,
  ModalStep,
  useModalSteps,
} from 'src/components/ModalContainer';
import { PLATFORM_PARTNER_ID } from 'src/constants/platform';
import {
  Macro,
  RecordFolder,
  RecordFolderArea,
  useChatMacros,
  useFavoriteMacros,
  useMacrosMethods,
  useRecordFolders,
  useSettingsMacros,
} from 'src/graphql';
import { resolveMacroSnippetSource } from 'src/graphql/entities/macro/macro.mappings';
import { MacroSnippetSource } from 'src/graphql/entities/macro/macro.types';
import {
  useCreateRecordFolder,
  useDeleteRecordFolder,
  useUpdateRecordFolder,
} from 'src/graphql/entities/record-folder';
import { useMacroFolderAssignments } from 'src/graphql/entities/record-folder-assignment';
import {
  FolderNameEditor,
  FolderRowActions,
  FolderRowMeta,
  MacroRowActions,
  NewFolderRowActions,
} from 'src/pages/Conversations/Chat/Macros/macros-modal/components/macro-list-rows';
import { FAVORITES_ID } from 'src/pages/Conversations/Chat/Macros/macros-modal/constants';
import { useMacroGrouping } from 'src/pages/Conversations/Chat/Macros/macros-modal/hooks/use-macro-grouping';
import { useMacrosAccess } from 'src/pages/Conversations/Chat/Macros/macros-modal/hooks/use-macros-access';
import { MacroEditorStep } from 'src/pages/Conversations/Chat/Macros/macros-modal/macro-editor-step';
import type { MacroFolderSection } from 'src/pages/Conversations/Chat/Macros/macros-modal/types';
import { Modals, useModalMethods } from 'src/store/modals';
import { useConfirmationModal, useNotifications } from 'src/utils/hooks';

const EMPTY_PREVIEW = 'No description provided.';

const NEW_FOLDER_ROW_ID = 'new-folder';

interface MacroListItem extends ModalListItem {
  macro?: Macro;
  folderId?: string;
}

interface FolderRename {
  folderId: string;
  name: string;
}

interface MacrosListStepProps {
  conversationId: string;
  partnerId: number | null;
  isManageMode?: boolean;
  onMacroSelected?: (macro: Macro, interpolatedMessage: string) => void;
}

const toggleId = (ids: Set<string>, id: string, include: boolean) => {
  const next = new Set(ids);
  if (include) {
    next.add(id);
  } else {
    next.delete(id);
  }
  return next;
};

/** The macro list: picks a macro into the composer, or, in manage mode, edits macros and folders. */
export const MacrosListStep = ({
  conversationId,
  partnerId,
  isManageMode = false,
  onMacroSelected,
}: MacrosListStepProps) => {
  const { pushStep } = useModalSteps();
  const { closeModal } = useModalMethods(Modals.MacrosModal);
  const { showErrorAlert, showSuccessAlert } = useNotifications();
  const { confirm } = useConfirmationModal();
  const { canManageInContext, canEditPlatformMacros, canEditMacro, resolvedPartnerId } =
    useMacrosAccess(partnerId);
  const { interpolateMacroTemplate } = useMacrosMethods();

  const [search, setSearch] = useState('');
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
  const [collapsedSearchFolderIds, setCollapsedSearchFolderIds] = useState<Set<string>>(
    new Set(),
  );
  const [newFolderName, setNewFolderName] = useState<string | null>(null);
  const [folderRename, setFolderRename] = useState<FolderRename | null>(null);
  const [deletingMacroId, setDeletingMacroId] = useState<string | null>(null);
  const [deletingFolderId, setDeletingFolderId] = useState<string | null>(null);

  const { chatMacros, loading: chatMacrosLoading } = useChatMacros({
    conversationPartnerId: partnerId,
    skip: isManageMode,
  });
  const {
    macros: settingsMacros,
    deleteMacro,
    loading: settingsMacrosLoading,
  } = useSettingsMacros({
    skip: !isManageMode || !canManageInContext,
    partnerId,
  });

  const assignmentPartnerIds = useMemo(
    () => unique([PLATFORM_PARTNER_ID, partnerId].filter(isNonNullish)),
    [partnerId],
  );
  const { recordFolderAssignments, isLoading: assignmentLoading } = useMacroFolderAssignments({
    partnerIds: assignmentPartnerIds,
  });
  const { recordFolders, isLoading: foldersLoading } = useRecordFolders({
    area: RecordFolderArea.Actions,
    partnerId,
    recordFolderAssignments,
  });
  const macroFolderAssignments = useMemo(
    () => recordFolderAssignments.filter((assignment) => assignment.entity === 'Macro'),
    [recordFolderAssignments],
  );

  const { createRecordFolder, isLoading: createFolderLoading } = useCreateRecordFolder();
  const { deleteRecordFolder, isLoading: deleteFolderLoading } = useDeleteRecordFolder();
  const { updateRecordFolder, isLoading: updateFolderLoading } = useUpdateRecordFolder();

  const macros = isManageMode ? settingsMacros : chatMacros;
  const visibleMacroIds = useMemo(() => new Set(macros.map((macro) => macro.id)), [macros]);
  const { favoriteMacroIds, toggleFavoriteMacro } = useFavoriteMacros({ macros });
  const favoriteMacroIdSet = useMemo(() => new Set(favoriteMacroIds), [favoriteMacroIds]);

  const isLoading =
    (isManageMode ? settingsMacrosLoading : chatMacrosLoading) ||
    foldersLoading ||
    assignmentLoading;
  const searchValue = search.toLowerCase();
  const isSearching = Boolean(searchValue);

  const { groupedFolders, effectiveExpandedFolders, visibleListEntries } = useMacroGrouping({
    macros,
    favoriteMacroIds,
    recordFolders,
    recordFolderAssignments: macroFolderAssignments,
    partnerIds: assignmentPartnerIds,
    expandedFolderIds,
    collapsedSearchFolderIds,
    isSearching,
    searchValue,
    isManageMode,
  });

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (!value) {
      setCollapsedSearchFolderIds(new Set());
    }
  };

  const handleExpandedChange = ({ folderId }: MacroListItem, expanded: boolean) => {
    if (!folderId) {
      return;
    }
    if (isSearching) {
      setCollapsedSearchFolderIds((ids) => toggleId(ids, folderId, !expanded));
    } else {
      setExpandedFolderIds((ids) => toggleId(ids, folderId, expanded));
    }
  };

  const openEditor = (macroId?: string) => {
    void pushStep(<MacroEditorStep macroId={macroId} partnerId={partnerId} />);
  };

  const handleEditMacro = (macro: Macro) => {
    if (!canEditMacro(macro)) {
      showErrorAlert('Platform macros can only be edited by platform admins.');
      return;
    }
    openEditor(macro.id);
  };

  const handlePickMacro = async (macro: Macro) => {
    if (!onMacroSelected) {
      return;
    }
    // An AI-prompt macro has no template to interpolate — the composer sends
    // its prompt to the writing assistant instead.
    const interpolatedMessage =
      resolveMacroSnippetSource(macro) === MacroSnippetSource.AiPrompt
        ? ''
        : await interpolateMacroTemplate({ text: macro.template, conversationId });

    onMacroSelected(macro, interpolatedMessage);
    closeModal();
  };

  const handleSelect = ({ macro }: MacroListItem) => {
    if (!macro) {
      return;
    }
    if (isManageMode) {
      handleEditMacro(macro);
    } else {
      void handlePickMacro(macro);
    }
  };

  const handleDeleteMacro = async (macro: Macro) => {
    if (deletingMacroId || !canManageInContext) {
      return;
    }
    if (!canEditMacro(macro)) {
      showErrorAlert('Platform macros can only be deleted by platform admins.');
      return;
    }

    const shouldDelete = await confirm({
      title: 'Delete macro?',
      body: `${macro.name ?? 'This macro'} will be deleted permanently for everyone.`,
      confirmLabel: 'Delete macro',
      danger: true,
    });
    if (!shouldDelete) {
      return;
    }

    setDeletingMacroId(macro.id);
    try {
      await deleteMacro(macro.id);
      showSuccessAlert('Macro deleted.');
    } catch (error) {
      console.error(error);
      showErrorAlert('Failed to delete macro. Please try again.');
    } finally {
      setDeletingMacroId(null);
    }
  };

  const handleDeleteFolder = async (folder: RecordFolder) => {
    if (deletingFolderId || !canManageInContext) {
      return;
    }

    const shouldDelete = await confirm({
      title: 'Delete folder?',
      body: `${folder.name} will be deleted, and its macros move to Uncategorized.`,
      confirmLabel: 'Delete folder',
      danger: true,
    });
    if (!shouldDelete) {
      return;
    }

    setDeletingFolderId(folder.id);
    try {
      await deleteRecordFolder(folder);
      showSuccessAlert('Folder deleted.');
    } catch (error) {
      console.error(error);
      showErrorAlert('Failed to delete folder. Please try again.');
    } finally {
      setDeletingFolderId(null);
    }
  };

  const handleRenameFolder = (folder: RecordFolder) => {
    const trimmedName = folderRename?.name.trim();
    if (!trimmedName || trimmedName === folder.name) {
      setFolderRename(null);
      return;
    }

    updateRecordFolder(folder, { name: trimmedName }, () => {
      setFolderRename(null);
      showSuccessAlert('Folder renamed.');
    });
  };

  const handleCreateFolder = async () => {
    const trimmedName = newFolderName?.trim();
    if (!trimmedName || createFolderLoading) {
      return;
    }

    const createdFolder = await createRecordFolder({
      name: trimmedName,
      isPrivate: false,
      icon: 'folder',
      area: RecordFolderArea.Actions,
      partnerId: resolvedPartnerId,
    });

    setNewFolderName(null);
    if (createdFolder) {
      setExpandedFolderIds((ids) => toggleId(ids, createdFolder.id, true));
    }
  };

  const toMacroItem = (id: string, macro: Macro, group?: string): MacroListItem => {
    const canEdit = isManageMode && canManageInContext && canEditMacro(macro);
    return {
      id,
      title: macro.name,
      description: macro.description?.trim() || EMPTY_PREVIEW,
      icon: <MessageSquare />,
      level: group ? 0 : 1,
      group,
      macro,
      actions: (
        <MacroRowActions
          isFavorite={favoriteMacroIdSet.has(macro.id)}
          canToggleFavorite={visibleMacroIds.has(macro.id)}
          onToggleFavorite={() => toggleFavoriteMacro(macro.id)}
          canEdit={canEdit}
          isDeleting={deletingMacroId === macro.id}
          onEdit={() => handleEditMacro(macro)}
          onDelete={() => void handleDeleteMacro(macro)}
        />
      ),
    };
  };

  const toFolderItem = (id: string, section: MacroFolderSection): MacroListItem => {
    const { folder } = section;
    const expanded = effectiveExpandedFolders.has(section.id);
    const isPlatform = folder.partnerId === PLATFORM_PARTNER_ID;
    const canEditFolder =
      isManageMode && canManageInContext && (canEditPlatformMacros || !isPlatform);
    const isRenaming = folderRename?.folderId === section.id;

    return {
      id,
      title: section.name,
      icon: expanded ? <FolderOpen /> : <Folder />,
      expanded,
      folderId: section.id,
      endAdornment: <FolderRowMeta count={section.macros.length} isPlatform={isPlatform} />,
      titleEditor: isRenaming ? (
        <FolderNameEditor
          value={folderRename.name}
          label="Folder name"
          onChange={(name) => setFolderRename({ folderId: section.id, name })}
          onSubmit={() => handleRenameFolder(folder)}
          onCancel={() => setFolderRename(null)}
          testId="macros-modal-folder-rename-input"
        />
      ) : undefined,
      actions: canEditFolder ? (
        <FolderRowActions
          isEditing={isRenaming}
          isSaving={updateFolderLoading}
          isDeleting={deletingFolderId === section.id || deleteFolderLoading}
          onStartRename={() => setFolderRename({ folderId: section.id, name: folder.name })}
          onSaveName={() => handleRenameFolder(folder)}
          onCancelRename={() => setFolderRename(null)}
          onDelete={() => void handleDeleteFolder(folder)}
        />
      ) : undefined,
    };
  };

  const newFolderItem: MacroListItem | null =
    newFolderName === null
      ? null
      : {
          id: NEW_FOLDER_ROW_ID,
          title: 'New folder',
          icon: <FolderPlus />,
          titleEditor: (
            <FolderNameEditor
              value={newFolderName}
              label="New folder name"
              onChange={setNewFolderName}
              onSubmit={() => void handleCreateFolder()}
              onCancel={() => setNewFolderName(null)}
              testId="macros-modal-folder-create-input"
            />
          ),
          actions: (
            <NewFolderRowActions
              canCreate={Boolean(newFolderName.trim())}
              isCreating={createFolderLoading}
              onCreate={() => void handleCreateFolder()}
              onCancel={() => setNewFolderName(null)}
            />
          ),
        };

  const sectionById = new Map(
    groupedFolders.folderSections.map((section) => [section.id, section]),
  );
  const listItems = visibleListEntries.flatMap<MacroListItem>((entry) => {
    if (entry.type === 'folder') {
      const section = entry.folderId ? sectionById.get(entry.folderId) : undefined;
      return section ? [toFolderItem(entry.id, section)] : [];
    }
    if (entry.folderId === FAVORITES_ID) {
      return [toMacroItem(entry.id, entry.macro, groupedFolders.favoritesSection?.name)];
    }
    if (entry.folderId === null) {
      return [toMacroItem(entry.id, entry.macro, groupedFolders.uncategorizedSection?.name)];
    }
    return [toMacroItem(entry.id, entry.macro)];
  });

  if (newFolderItem) {
    const favoritesName = groupedFolders.favoritesSection?.name;
    const afterFavorites = favoritesName
      ? listItems.findIndex((item) => item.group !== favoritesName)
      : 0;
    listItems.splice(
      afterFavorites === -1 ? listItems.length : afterFavorites,
      0,
      newFolderItem,
    );
  }

  const manageActions: ModalHeaderAction[] = [
    {
      label: 'New macro',
      icon: <Plus />,
      onClick: () => openEditor(),
      testId: 'macros-modal-new-macro',
    },
    {
      label: 'New folder',
      icon: <FolderPlus />,
      onClick: () => setNewFolderName(''),
      testId: 'macros-modal-new-folder',
    },
  ];
  const pickActions: ModalHeaderAction[] = [
    {
      label: 'Manage macros',
      icon: <Settings />,
      onClick: () => {
        void pushStep(
          <MacrosListStep
            conversationId={conversationId}
            partnerId={partnerId}
            isManageMode
          />,
        );
      },
      testId: 'macros-modal-manage-toggle',
    },
  ];
  const pickHeaderActions = canManageInContext ? pickActions : undefined;

  return (
    <ModalStep
      title={isManageMode ? 'Manage macros' : 'Macros'}
      headerActions={isManageMode ? manageActions : pickHeaderActions}
    >
      <ModalList
        items={listItems}
        shouldFilter={false}
        onSearchChange={handleSearchChange}
        onSelect={handleSelect}
        onExpandedChange={handleExpandedChange}
        loading={isLoading}
        placeholder="Search macros"
        emptyText={
          isSearching
            ? 'No macros match your search.'
            : 'No macros yet. Create one in manage mode.'
        }
        testId={isManageMode ? 'macros-modal-manage' : 'macros-modal'}
      />
    </ModalStep>
  );
};
